import { Share } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { type Referral } from '../../../lib/use-referrals';
import { Modal } from '../../Modal';
import { Badge, Button, Card, Typography } from '../../symphony/ui';
import { EarnCreditsSection } from './EarnCreditsSection';
import { InviteLinkRow } from './InviteLinkRow';
import { ReferralAvatar } from './ReferralAvatar';
import type { ShareAppOutcome } from './use-share-app';
import styles from './ShareAppModalMobile.module.css';

export interface ShareAppModalViewProps {
  open: boolean;
  close: () => void;
  share: () => Promise<ShareAppOutcome>;
  steps: { title: string; body: string }[];
  joined: number;
  /** Already resolved against the SERVED referral amount — no-amount copy when the rules read failed. */
  earnedTitle: string;
  earnedSubtitle: string;
  /** What one referral pays, or `null` while unknown — the per-referral badge is dropped then. */
  referralCredits: number | null;
  /** The referral program's closing sentence, or `null` while either amount it names is unknown. */
  referralNote: string | null;
  showReferrals: boolean;
  referrals: Referral[];
  shareLink: string;
  loading: boolean;
}

// The mobile "Share the app" screen (Figma 5339-2840). Same referral flow as the desktop modal —
// the shared logic (referrals, earned runs, the real OS share) is computed once in ShareAppModal
// and handed down — but a full-screen phone layout: a large hero heading, the earned-runs reward as
// a green agents-tinted callout, the two invite steps, a full-width "Share Symphony" CTA, and the
// "Successful referrals" list below it. Presented through the shared Modal's full-screen-on-small
// variant, so the ✕ (which fires the close BI via `close`) is the dismiss.
//
// TWO PROGRAMS, IN ORDER. Everything from the heading down to the referral note is PROGRAM 1 (invite a
// friend); `EarnCreditsSection` at the bottom is PROGRAM 2 (post about us), separated by the hairline it
// draws itself. The referral history used to render AFTER that section and the referral note INSIDE it,
// which interleaved the two and made the screen read as one mixed-up offer. Keep PROGRAM 1 contiguous.
export function ShareAppModalMobile({
  open,
  close,
  share,
  steps,
  joined,
  earnedTitle,
  earnedSubtitle,
  referralCredits,
  referralNote,
  showReferrals,
  referrals,
  shareLink,
  loading,
}: ShareAppModalViewProps) {
  const { t } = useTranslation();

  return (
    <Modal open={open} onClose={close} labelledBy="share-app-title" scrollBody>
      <div className={styles.screen}>
        <header className={styles.head}>
          <Typography variant="h1" component="h1" id="share-app-title">
            {t('settings.shareApp.heading')}
          </Typography>
          <Typography variant="p2" component="p">
            {t('settings.shareApp.subtitle')}
          </Typography>
        </header>

        <div className={styles.reward}>
          <Typography variant="p1" component="span">
            {earnedTitle}
          </Typography>
          <Typography variant="p2" component="span" color="secondary">
            {earnedSubtitle}
          </Typography>
        </div>

        <div className={styles.steps}>
          {steps.map((s, i) => (
            <div key={i} className={styles.step}>
              <span aria-hidden className={styles.stepNum}>
                {i + 1}
              </span>
              <span className={styles.stepText}>
                <Typography variant="h4" component="span">
                  {s.title}
                </Typography>
                <Typography variant="p2" component="span" color="secondary">
                  {s.body}
                </Typography>
              </span>
            </div>
          ))}
        </div>

        <InviteLinkRow shareLink={shareLink} loading={loading} />

        {/* Only the phone offers this: `navigator.share` opens a real OS sheet here, so it reaches
            WhatsApp/Messages directly, where on a desktop it would resolve to the same clipboard write
            Copy already performs. Outlined because Copy is this screen's primary action, and disabled
            without a link because sharing one that carries no sharer id credits nobody. */}
        <Button
          variant="outlined"
          fullWidth
          size="large"
          startIcon={<Share size={16} aria-hidden />}
          disabled={!shareLink || loading}
          onClick={() => void share()}
        >
          {t('settings.shareApp.cta')}
        </Button>

        {/* PROGRAM 1 — invite a friend — ends here, and it ends CONTIGUOUSLY: the heading, the earned
            callout, the two steps, the link, the CTA, this referral history and the closing note below
            it are one uninterrupted run. The referral history used to render AFTER the social section,
            so referral content appeared under a social heading and the screen read as one mixed-up
            offer instead of two programs. */}
        {showReferrals && (
          <section className={styles.referrals}>
            <div className={styles.referralHead}>
              <Typography variant="p1" component="span">
                {t('settings.shareApp.successfulReferrals')}
              </Typography>
              <Typography variant="p2" component="span" color="secondary">
                {t('settings.shareApp.joined', { count: joined })}
              </Typography>
            </div>
            {referrals.map((r) => (
              <Card key={r.id} variant="sunken" size="sm" className={styles.referralCard}>
                {/* ANONYMOUS — see the note in ShareAppModal: the server no longer sends a name. */}
                <ReferralAvatar name={r.id} size={40} anonymous />
                <Typography variant="h4" component="span" className={styles.referralName} noWrap>
                  {t('settings.shareApp.someoneJoined')}
                </Typography>
                {referralCredits !== null && (
                  <Badge variant="agents" size="small" label={t('settings.shareApp.reward', { count: referralCredits })} />
                )}
              </Card>
            ))}
          </section>
        )}

        {/* Outside the `showReferrals` gate on purpose: the sentence explains what the program PAYS, so
            it is exactly what someone with no referrals yet needs to read. */}
        {referralNote && (
          <Typography variant="p3" component="p" color="secondary">
            {referralNote}
          </Typography>
        )}

        {/* PROGRAM 2 — social posts. It draws the rule that separates it from everything above (its own
            top border, so the line disappears with the section when no networks are served). */}
        <EarnCreditsSection open={open} className={styles.earnCredits} />
      </div>
    </Modal>
  );
}
